import { getDoc, setDoc } from './store';
import type { ModDoc, ModId, Reg } from './types';
import { uid } from '../lib/date';

// Motor de fichas: cada aba nova (Viagens, Obras, Saúde...) é uma lista de registros (Reg) agrupados em coleções.
const agora = () => new Date().toISOString();
const mut = (m: ModId, fn: (d: ModDoc) => ModDoc) => setDoc(m, d => fn({ regs: d?.regs ?? [] }));

export function novoReg(col: string, pai: string | null = null, c: Record<string, any> = {}): Reg {
  const t = agora();
  return { id: uid(), col, pai, c, criado: t, alt: t };
}
// Grupo de uma ficha nova (dados compartilhados, 0.25). Definido por lib/compartilhar para não criar ciclo de imports.
type RegraNova = (m: ModId, r: Reg, regs: Reg[]) => string | null;
let grupoDaNova: RegraNova = () => null;
export const definirGrupoDaNova = (f: RegraNova) => { grupoDaNova = f; };
function comGrupo(m: ModId, r: Reg, regs: Reg[]): Reg {
  const g = grupoDaNova(m, r, regs);
  return g ? { ...r, g } : r;
}

export const regsDe = (m: ModId) => getDoc(m)?.regs ?? [];
export const regPorId = (m: ModId, id: string) => regsDe(m).find(r => r.id === id) ?? null;

export function salvarReg(m: ModId, r: Reg) {
  const novo = { ...r, alt: agora() };
  mut(m, d => ({ regs: d.regs.some(x => x.id === r.id) ? d.regs.map(x => (x.id === r.id ? novo : x)) : [...d.regs, comGrupo(m, novo, d.regs)] }));
}
export function salvarVarios(m: ModId, lista: Reg[]) {
  if (!lista.length) return;
  const ids = new Map(lista.map(r => [r.id, { ...r, alt: agora() }]));
  mut(m, d => {
    const novas: Reg[] = [];
    for (const r of ids.values()) if (!d.regs.some(x => x.id === r.id)) novas.push(comGrupo(m, r, [...d.regs, ...novas]));
    return { regs: [...d.regs.map(x => ids.get(x.id) ?? x), ...novas] };
  });
}
/** Nova ordem escolhida arrastando (0.34). */
export function ordenarRegs(m: ModId, ids: string[]) {
  const pos = new Map(ids.map((id, i) => [id, i]));
  mut(m, d => ({ regs: d.regs.map(x => (pos.has(x.id) && x.ordem !== pos.get(x.id) ? { ...x, ordem: pos.get(x.id), alt: agora() } : x)) }));
}
/** Muda só alguns campos (ex.: marcar como devolvido, mover no Kanban). */
export const mudarCampos = (m: ModId, id: string, c: Record<string, any>) =>
  mut(m, d => ({ regs: d.regs.map(x => (x.id === id ? { ...x, c: { ...x.c, ...c }, alt: agora() } : x)) }));
export const mudarReg = (m: ModId, id: string, fn: (r: Reg) => Reg) =>
  mut(m, d => ({ regs: d.regs.map(x => (x.id === id ? { ...fn(x), alt: agora() } : x)) }));

/** Exclui o registro e tudo o que está dentro dele (ex.: a viagem com o roteiro e as reservas). */
export function excluirReg(m: ModId, id: string) {
  mut(m, d => {
    const fora = new Set([id]);
    let mudou = true;
    while (mudou) { mudou = false; for (const r of d.regs) if (r.pai && fora.has(r.pai) && !fora.has(r.id)) { fora.add(r.id); mudou = true; } }
    return { regs: d.regs.filter(r => !fora.has(r.id)) };
  });
}
/** Todos os registros que serão excluídos junto (para avisar quantos). */
export function descendentes(m: ModId, id: string) {
  const regs = regsDe(m);
  const out: Reg[] = [];
  const fila = [id];
  while (fila.length) { const p = fila.shift()!; for (const r of regs) if (r.pai === p) { out.push(r); fila.push(r.id); } }
  return out;
}
